import { useIntl } from 'react-intl';
import { MovieWatchParamsFragment } from '@funtech-int/kp-graphql-sdk/yango-movies-www';

import { SubtitlesConfig } from '@/scenarios/legacy-player/components/Player';
import { Option } from '@/scenarios/legacy-player/components/PlayerSkin/parts/OptionList/OptionList.types';
import { getTextTrackOption } from '@/scenarios/legacy-player/components/PlayerSkin/parts/Tracks/Subtitles/helpers/get-text-track-option';

import { useDisableWhenAlone } from '../../hooks/useDisableWhenAlone';
import { Subtitles, useAudioTrackContext } from '../../Tracks.types';
import { useAddEmptyOption } from '../useAddEmptyOption';

import { useLeftJoinSubtitles } from './useLeftJoinSubtitles';

interface TextTrackOption extends Option<string> {
    lang: string;
}

const compareSubtitles =
    (watchParams: MovieWatchParamsFragment['watchParams'] | undefined) =>
    (track1: Subtitles, track2: Subtitles) => {
        if (track1.lang === watchParams?.subtitleLanguage) {
            return -1;
        }
        if (track2.lang === watchParams?.subtitleLanguage) {
            return 1;
        }
        if (track1.info?.index !== undefined && track2.info?.index !== undefined) {
            return track1.info.index - track2.info.index;
        }
        return track1.info?.index === undefined ? 1 : -1;
    };

const checkShow: (
    track: Subtitles,
    watchParams: MovieWatchParamsFragment['watchParams'] | undefined
) => boolean = (track, watchParams) =>
    track.info === undefined ||
    track.info.visibleByDefault ||
    track.lang === watchParams?.subtitleLanguage;

interface Params {
    textTracksRaw: readonly Subtitles[];
    showAll: boolean;
    isSubtitlesToggleEnabled: boolean;
    subtitles: SubtitlesConfig;
}

export const usePreparedTextTracks = ({
    textTracksRaw,
    showAll,
    isSubtitlesToggleEnabled,
    subtitles,
}: Params): {
    textTracks: readonly TextTrackOption[];
    hasInvisibleByDefault: boolean;
} => {
    const intl = useIntl();

    const { watchParams } = useAudioTrackContext();

    let textTracks = Array.from(useLeftJoinSubtitles(textTracksRaw, subtitles)).map(
        (track) => track[1]
    );

    textTracks = textTracks.map((track) => ({
        ...track,
        ...(track.info ? getTextTrackOption(intl, track.info) : {}),
    }));

    const smallList = textTracks.length <= 3;

    const hasInvisibleByDefault =
        !smallList && textTracks.some((track) => !checkShow(track, watchParams));

    if (!smallList && !showAll) {
        textTracks = textTracks.filter((track) => checkShow(track, watchParams));
    }

    textTracks.sort(compareSubtitles(watchParams));

    textTracks = useDisableWhenAlone(
        useAddEmptyOption(textTracks, isSubtitlesToggleEnabled)
    ) as Subtitles[];

    return { textTracks, hasInvisibleByDefault };
};
